import React, { useState } from 'react';
import { X } from 'lucide-react';
import RebateTierInput from './RebateTierInput';

const CustomerProfileModal = ({
    selectedCustomer,
    formData,
    setFormData,
    messages,
    newMessage,
    setNewMessage,
    handleSendMessage,
    handleSaveProfile,
    onClose,
    uinfo
}) => {
    const [activeTab, setActiveTab] = useState('messages');

    const calculateRebate = (amount, tiers, isGlobalPercent) => {
        if (tiers.length === 0 || amount <= 0) return 0;

        // Sort tiers by target amount in ascending order
        const sortedTiers = [...tiers].sort((a, b) => a.target - b.target);

        if (isGlobalPercent) {
            // Global percentage: use the highest percentage applicable to the amount
            const applicableTier = sortedTiers.reverse().find(tier => amount >= tier.target);
            return applicableTier ? amount * (applicableTier.percent / 100) : 0;
        } else {
            // Tier-based calculation
            let rebate = 0;
            let remainingAmount = amount;

            for (let i = 0; i < sortedTiers.length; i++) {
                const tier = sortedTiers[i];
                const nextTarget = sortedTiers[i + 1]?.target || Infinity; // Next tier target or infinity

                if (amount < tier.target) break; // Stop if amount is below the current tier

                // Calculate the amount in this tier's bracket
                const amountInBracket = Math.min(remainingAmount, nextTarget - tier.target);

                // Add rebate for this tier
                rebate += amountInBracket * (tier.percent / 100);

                // Update remaining amount
                remainingAmount -= amountInBracket;
            }

            return rebate;
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-6">
                    {/* Left side: Editable details */}
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">
                                Profile for {selectedCustomer?.Name}
                            </h2>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="grid gap-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-medium">Number of Ramps</label>
                                    <select
                                        value={formData.numberOfRamps}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            numberOfRamps: parseInt(e.target.value)
                                        }))}
                                        className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        {[...Array(21)].map((_, i) => (
                                            <option key={i} value={i}>{i}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-medium">Number of Technicians</label>
                                    <select
                                        value={formData.numberOfTechnicians}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            numberOfTechnicians: parseInt(e.target.value)
                                        }))}
                                        className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        {[...Array(21)].map((_, i) => (
                                            <option key={i} value={i}>{i}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium">Services Offered</label>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.providesMOT}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                providesMOT: e.target.checked
                                            }))}
                                            className="rounded"
                                        />
                                        MOT
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.providesOilService}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                providesOilService: e.target.checked
                                            }))}
                                            className="rounded"
                                        />
                                        Oil Service
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.providesEVService}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                providesEVService: e.target.checked
                                            }))}
                                            className="rounded"
                                        />
                                        EV Service
                                    </label>
                                </div>
                            </div>

                            <div className="flex flex-col gap-4">
                                <label className="text-sm font-medium">Opening Hours</label>
                                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                                    <div key={day} className="grid grid-cols-3 gap-4 items-center">
                                        <span className="text-sm capitalize">{day}</span>
                                        <input
                                            type="time"
                                            value={formData.openingHours[day]?.open || ''}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                openingHours: {
                                                    ...prev.openingHours,
                                                    [day]: {
                                                        ...prev.openingHours[day],
                                                        open: e.target.value
                                                    }
                                                }
                                            }))}
                                            className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        <input
                                            type="time"
                                            value={formData.openingHours[day]?.close || ''}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                openingHours: {
                                                    ...prev.openingHours,
                                                    [day]: {
                                                        ...prev.openingHours[day],
                                                        close: e.target.value
                                                    }
                                                }
                                            }))}
                                            className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                ))}
                            </div>


                            <button
                                onClick={handleSaveProfile}
                                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 mt-4"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>

                    {/* Right side: Tabbed section */}
                    <div className="flex flex-col h-full">
                        {/* Tab buttons */}
                        <div className="flex border-b">
                            <button
                                className={`px-4 py-2 font-medium ${activeTab === 'messages'
                                    ? 'text-blue-600 border-b-2 border-blue-600'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                onClick={() => setActiveTab('messages')}
                            >
                                Messages
                            </button>
                            {uinfo.Status == 'admin' ?
                                <button
                                    className={`px-4 py-2 font-medium ${activeTab === 'rebates'
                                        ? 'text-blue-600 border-b-2 border-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    onClick={() => setActiveTab('rebates')}
                                >
                                    Rebate Structure
                                </button>
                                : null}
                        </div>

                        {/* Tab content */}
                        <div className="flex-1 overflow-hidden">
                            {activeTab === 'messages' ? (
                                <div className="h-full flex flex-col">
                                    <div className="flex-1 overflow-y-auto">
                                        <div className="p-4 space-y-3">
                                            {messages.length > 0 ? (
                                                messages.map((msg, index) => (
                                                    <div
                                                        key={index}
                                                        className={`flex ${msg.staffMember === uinfo?.Name ? "justify-end" : "justify-start"}`}
                                                    >
                                                        <div
                                                            className={`max-w-[75%] p-3 rounded-lg ${msg.staffMember === uinfo?.Name
                                                                ? "bg-green-500 text-white"
                                                                : "bg-gray-200 text-gray-900"
                                                                }`}
                                                        >
                                                            <div className="text-lg font-medium">{msg.staffMember}</div>
                                                            <div className="text-sm">{msg.message}</div>
                                                            <div className="text-xs text-gray-400 mt-1">
                                                                {new Date(msg.timestamp).toLocaleString()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-sm text-gray-500">No messages yet.</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="p-4 border-t bg-white">
                                        <textarea
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            rows={3}
                                            placeholder="Add a new message..."
                                        />
                                        <button
                                            onClick={handleSendMessage}
                                            className="mt-2 w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                        >
                                            Send
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="h-full overflow-y-auto p-4">
                                    <div className="flex flex-col gap-4">
                                        <div className="flex items-center gap-4">
                                            <label className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.rebateStructure.isGlobalPercent}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        rebateStructure: {
                                                            ...prev.rebateStructure,
                                                            isGlobalPercent: e.target.checked
                                                        }
                                                    }))}
                                                    className="rounded"
                                                />
                                                <span className="text-sm">Use global percentage (highest tier applies to all sales)</span>
                                            </label>
                                        </div>

                                        <div className="space-y-2">
                                            {formData.rebateStructure.tiers.map((tier, index) => (
                                                <RebateTierInput
                                                    key={index}
                                                    tier={tier}
                                                    index={index}
                                                    tiers={formData.rebateStructure.tiers}
                                                    onUpdate={(index, updatedTier) => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            rebateStructure: {
                                                                ...prev.rebateStructure,
                                                                tiers: prev.rebateStructure.tiers.map((t, i) =>
                                                                    i === index ? updatedTier : t
                                                                )
                                                            }
                                                        }));
                                                    }}
                                                    onRemove={(index) => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            rebateStructure: {
                                                                ...prev.rebateStructure,
                                                                tiers: prev.rebateStructure.tiers.filter((_, i) => i !== index)
                                                            }
                                                        }));
                                                    }}
                                                />
                                            ))}
                                        </div>

                                        <button
                                            onClick={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    rebateStructure: {
                                                        ...prev.rebateStructure,
                                                        tiers: [
                                                            ...prev.rebateStructure.tiers,
                                                            { target: '', percent: '' }
                                                        ]
                                                    }
                                                }));
                                            }}
                                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 w-fit"
                                        >
                                            Add Tier
                                        </button>

                                        {formData.rebateStructure.tiers.length > 0 && (
                                            <div className="mt-4 p-4 bg-gray-50 rounded">
                                                <h4 className="font-medium mb-2">Example Calculations:</h4>
                                                <div className="space-y-2 text-sm">
                                                    {[1000, 2000, 5000].map(amount => {
                                                        const rebate = calculateRebate(
                                                            amount,
                                                            formData.rebateStructure.tiers,
                                                            formData.rebateStructure.isGlobalPercent
                                                        );

                                                        return (
                                                            <div key={amount}>
                                                                £{amount.toLocaleString()}: £{rebate.toFixed(2)} rebate
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerProfileModal;